
import {
  Component,
  DestroyRef,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  Optional,
  ViewChild,
  DOCUMENT
} from '@angular/core';
import { RouterModule } from '@angular/router';
import {
  FocusConfig,
  HamburgerMenuService,
  KeyboardFocusModule,
  KeyboardFocusService,
  OutletModule,
  OutletRefModule,
  PageLayoutModule,
  PageSlotModule,
  SkipFocusConfig,
  SkipLinkComponent,
  SkipLinkModule,
  SkipLinkService,
  StorefrontOutlets,
  ThemeModule,
} from '@spartacus/storefront';
import { ToasterModule } from '../../../components/toaster/toaster.module';
import { CustomGlobalMessageComponent } from '../../../core/handlers/custom-global-message/custom-global-message.component';
import { LoaderComponent } from '../../../components/loader/loader.component';
import { HeaderModule } from './misc/header/header.module';
import { ModalListModule } from '../../../components/modal-list/modal-list.module';
import { CmsComponentsModule } from './misc/cms-components/cms-components.module';
import { AccountSelectionStoreModule } from '../user/account/components/login-form/account-selection/store/account-selection-store.module';
import { ProductComparatorFloattingModule } from '../product/product-comparator-floating/product-comparator.module';
import { FeatureConfigService, RoutingService } from '@spartacus/core';
import { distinctUntilChanged, Observable, Subscription, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'custom-cx-store-front',
  standalone: true,
  imports: [
    RouterModule,
    CustomGlobalMessageComponent,
    OutletModule,
    OutletRefModule,
    PageLayoutModule,
    PageSlotModule,
    KeyboardFocusModule,
    SkipLinkModule,
    ThemeModule,
    AccountSelectionStoreModule,
    HeaderModule,
    ToasterModule,
    ModalListModule,
    LoaderComponent,
    CmsComponentsModule,
    ProductComparatorFloattingModule
],
  templateUrl: './store-front.component.html',
  styleUrl: './store-front.component.scss',
})
export class CustomStoreFrontComponent {
  navigateSubscription?: Subscription;
  focusConfig: FocusConfig = { disableMouseFocus: true, trap: false };
  skipFocusConfig: SkipFocusConfig = {
    isEnabled: false,
    activeElementSelectors: ['button.cx-hamburger'],
  };
  isExpanded$: Observable<boolean> = this.hamburgerMenuService.isExpanded;

  readonly StorefrontOutlets = StorefrontOutlets;

  private featureConfigService = inject(FeatureConfigService);
  protected destroyRef = inject(DestroyRef);
  @Optional() protected document = inject(DOCUMENT, {
    optional: true,
  });
  @Optional() protected skipLinkService = inject(SkipLinkService, {
    optional: true,
  });

  @HostBinding('class.start-navigating') startNavigating?: boolean;
  @HostBinding('class.stop-navigating') stopNavigating?: boolean;

  // TODO: (CXSPA-7464) - Remove feature flags and following bindings next major release.
  @HostBinding('attr.role') role = this?.featureConfigService.isEnabled('a11yScreenReaderBloatFix')
    ? null
    : 'presentation';

  // required by esc focus
  @HostBinding('tabindex') tabindex = this?.featureConfigService.isEnabled('a11yScreenReaderBloatFix') ? '-1' : '0';

  @ViewChild(SkipLinkComponent) child?: SkipLinkComponent;

  private keyboardFocusConfig: FocusConfig = {
    focusOnEscape: true,
    focusOnDoubleEscape: true,
  };

  @HostListener('keydown.escape', ['$event'])
  handleEscape(event: Event): void {
    this.keyboardFocusService.handleEscape(this.elementRef.nativeElement, this.keyboardFocusConfig, event as KeyboardEvent);
  }

  constructor(
    private hamburgerMenuService: HamburgerMenuService,
    private routingService: RoutingService,
    protected elementRef: ElementRef<HTMLElement>,
    protected keyboardFocusService: KeyboardFocusService,
  ) {
    // useFeatureStyles('a11yImproveContrast');
    // useFeatureStyles('cmsBottomHeaderSlotUsingFlexStyles');
    // useFeatureStyles('headerLayoutForSmallerViewports');
    // useFeatureStyles('a11yPdpGridArrangement');
    // useFeatureStyles('a11yKeyboardFocusInSearchBox');
  }

  ngOnInit(): void {
    this.navigateSubscription = this.routingService.isNavigating().subscribe(val => this.onNavigation(val));
    if (this.featureConfigService.isEnabled('a11yMobileFocusOnFirstNavigationItem')) {
      this.isExpanded$ = this.hamburgerMenuService.isExpanded.pipe(
        tap(isExpanded => {
          if (isExpanded) {
            this.focusOnFirstNavigationItem();
          }
        }),
      );
    }

    if (this.featureConfigService.isEnabled('a11yHamburgerMenuTrapFocus')) {
      this.trapFocusOnMenuIfExpanded();
    }
  }

  collapseMenuIfClickOutside(event: any): void {
    const element = event.target;
    if (element.nodeName.toLowerCase() === 'header' && element.className.includes('is-expanded')) {
      this.collapseMenu();
    }
  }

  collapseMenu(): void {
    this.hamburgerMenuService.toggle(true);
  }

  protected trapFocusOnMenuIfExpanded(): void {
    this.isExpanded$.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe(isExpanded => {
      this.focusConfig = { ...this.focusConfig, trap: isExpanded };
      this.skipFocusConfig = {
        ...this.skipFocusConfig,
        isEnabled: isExpanded,
      };
    });
  }

  protected focusOnFirstNavigationItem() {
    const closestNavigationUi = this.elementRef.nativeElement.querySelector('header cx-navigation-ui');
    const focusable = closestNavigationUi?.querySelector<HTMLElement>('li:not(.back) button, [tabindex="0"]');
    if (focusable) {
      setTimeout(() => focusable.focus());
    }
  }

  ngOnDestroy(): void {
    if (this.navigateSubscription) {
      this.navigateSubscription.unsubscribe();
    }
  }

  protected onNavigation(isNavigating: boolean): void {
    this.startNavigating = isNavigating === true;
    this.stopNavigating = isNavigating === false;

    // After clicking a link the focus should move to the first available item in the main content area.
    if (
      this.featureConfigService.isEnabled('a11yResetFocusAfterNavigating') &&
      this.stopNavigating &&
      this.document?.activeElement !== this.document?.body
    ) {
      this.skipLinkService?.scrollToTarget('cx-main');
    }
  }
}
